import { Languages } from '@wicadu/langscope'

import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Subir y gestionar imágenes de productos',
    seo_description:
      'Aprende cómo agregar y actualizar imágenes de productos en tu tienda en línea con wicadu. Mejora su apariencia y muestra tus productos de forma profesional',
    seo_keywords:
      'subir imágenes de productos, agregar imágenes de productos wicadu, guía de gestión de imágenes wicadu, optimizar imágenes para tienda online, cargar imágenes productos wicadu, imágenes tienda online',
    page_id: PagesId.ManageItemsMedia,
    breadcrumbs: {
      home: 'Inicio',
      items_home: 'Productos e inventario',
      manage_items_media: 'Subir y gestionar imágenes de productos',
    },
    article: {
      title: 'Subir y gestionar imágenes de productos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar <em>inventario</em> y <em>productos</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
        '<strong>Vendedores</strong> o <strong>Abastecedores</strong> con permisos para <em>crear productos</em>, <em>actualizar características</em> y <em>asignar imágenes</em>.'
      ],
      about: {
        title: 'Acerca de subir y gestionar imágenes de productos',
        content_type: ContentType.Paragraph,
        content: [
          'Las <strong>imágenes de producto</strong> son clave para presentar tu inventario de forma <em>atractiva</em>, <em>generar confianza</em> y aumentar las conversiones en tu tienda en línea. <strong>Subir imágenes</strong> claras, profesionales y actualizadas no solo mejora la apariencia visual de tu tienda, sino que también ayuda a los clientes a tomar decisiones de compra informadas',
          'En esta guía aprenderás cómo subir nuevas imágenes de productos o actualizar las existentes paso a paso.'
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Antes de agregar o actualizar imágenes de productos, asegúrate de contar con lo siguiente:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Puedes subir imágenes a tus productos siempre que tengan uno de estos formatos: PNG, JPEG, SVG, WEBP',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Las imágenes pueden pesar hasta 2 MB, pero se recomienda que sean de menos de 500 kB para que tu tienda en línea cargue más rápido.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Recomendamos de 450x450 píxeles con una relación de aspecto 1:1 para mayor claridad'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Mantén <strong>copias de tus imágenes</strong> originales en caso de que necesites revertir cambios o solucionar problemas.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            class: 'msg-alert',
            content: [
              `<strong>Nota</strong>: Visita nuestra <a href=${process.env.STORES_WEBAPP_HOST} title="Guía de formatos y recomendaciones de imágenes">guía de formatos y recomendaciones de imágenes</a> para más detalles sobre tamaños, compresión y buenas prácticas`,
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Pasos para subir imágenes',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Accede a la funcionalidad de <em>Inventario</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona un producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Busca el producto desde la lista o con el buscador, haz clic sobre su nombre y luego en el ícono de imagen que aparece debajo del título para abrir el modo de edición de imágenes'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Sube las imágenes: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En el modo de edición de imágenes, haz clic en <em>"<strong>Selecciona una imagen de tu galería</strong>"</em>. Según el dispositivo que estés utilizando:',
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>iPhone y Android</b>: Al presionar el botón, se abrirá un menú con opciones como <em>Tomar una foto</em> o <em>Elegir desde la galería</em>. Selecciona el método que prefieras para agregar tu imagen.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Laptop</b>: Al hacer clic, se abrirá el <em>explorador de archivos</em> de tu sistema. Navega hasta la carpeta correspondiente y selecciona la imagen que deseas subir.',
                    ],
                  },
                ],
              },

            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Imagen guardada automáticamente: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Una vez seleccionada, la imagen se cargará y se guardará automáticamente en el producto. Puedes repetir este proceso para subir más imágenes o reorganizarlas si es necesario.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Nota</strong>: Si estás en el <strong>plan gratuito</strong>, solo puedes subir <strong>una imagen por producto</strong>. Para agregar más imágenes, considera actualizar tu plan desde la sección de Configuración > Planes y facturación`,
                ],
              },
            ],
          },
        ],
      },
      how_to_update: {
        title: 'Pasos para actualizar imágenes',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Accede a la funcionalidad de <em>Inventario</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona un producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Busca el producto desde la lista o con el buscador, haz clic sobre su nombre y luego en el ícono de imagen que aparece debajo del título para abrir el modo de edición de imágenes'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona la imagen a actualizar:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En el carrusel de imágenes, haz clic sobre la imagen que deseas reemplazar.',
                  'Una vez seleccionada, presiona el botón "<strong>Actualizar imagen</strong>" ubicado en la parte inferior de la imagen.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Elige la nueva imagen:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Según el dispositivo que estés utilizando:'
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>iPhone y Android</b>: Al presionar el botón, se abrirá un menú con opciones como <em>Tomar una foto</em> o <em>Elegir desde la galería</em>. Selecciona el método que prefieras para agregar tu imagen.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Laptop</b>: Al hacer clic, se abrirá el <em>explorador de archivos</em> de tu sistema. Navega hasta la carpeta correspondiente y selecciona la imagen que deseas subir.',
                    ],
                  },
                ],
              },

            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Imagen guardada automáticamente: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'La imagen se reemplazará y se guardará automáticamente en el producto.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Nota</strong>: Si estás en el <strong>plan gratuito</strong>, solo puedes tener <strong>una imagen por producto</strong>, por lo que deberás reemplazar la existente si deseas actualizarla.`,
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Pasos para eliminar imágenes',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Accede a la funcionalidad de <em>Inventario</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona un producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Busca el producto desde la lista o con el buscador, haz clic sobre su nombre y luego en el ícono de imagen que aparece debajo del título para abrir el modo de edición de imágenes'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Elimina la imagen que desees:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En el carrusel de imágenes, coloca el cursor (o presiona la imagen si estás en móvil) y haz clic en el ícono de papelera en la esquina superior derecha para eliminarla del producto.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Nota</strong>: Una vez eliminada, la imagen no se puede recuperar desde wicadu. Te recomendamos mantener una copia de seguridad en tu dispositivo si crees que podrías necesitarla más adelante.`,
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ahora que sabes cómo subir, actualizar y eliminar imágenes de productos, considera realizar las siguientes acciones para mejorar la presentación de tu tienda:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verifica los detalles e imágenes en tu tienda en línea:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Después de subir, actualizar o eliminar imágenes, revisa cómo se visualizan los productos en la tienda para asegurarte de que todo esté correcto y atractivo para tus clientes.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Considera actualizar tu plan si necesitas más imágenes: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Los planes pagos permiten subir más de una imagen por producto. Si estás en el plan gratuito, puedes cambiar de plan desde Configuración > Planes y facturación.'
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No puedo subir una imagen</strong>: Asegúrate de que esté en formato <strong>PNG</strong>, <strong>JPEG</strong>, <strong>SVG</strong> o <strong>WEBP</strong> y que pese menos de 2 MB.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Solo puedo subir una imagen por producto</strong>: El <strong>plan gratuito</strong> permite solo una imagen por producto. Actualiza tu plan para añadir más.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Reemplacé una imagen pero no se ve el cambio</strong>: Intenta actualizar la caché de tu navegador o vuelve a cargar la página. Verifica que hayas seleccionado la imagen correcta.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Subí una imagen pero se ve borrosa</strong>: Usa una imagen de al menos 450x450 píxeles y preferiblemente de menos de 500 kB para buena calidad y carga rápida.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>No se elimina la imagen</strong>: Verifica tu conexión a internet. Si el problema persiste, contacta al equipo de
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.Spanish)} title="soporte de wicadu">Soporte de wicadu</a> para asistencia.`,
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'Upload and manage item images',
    seo_description:
      "Learn how to upload, update, and delete product images in your wicadu online store. Enhance your store's appearance and showcase products professionally.",
    seo_keywords:
      'upload product images, update product images, delete product images, product gallery wicadu, product photo limit, wicadu inventory images, manage ecommerce images, replace image wicadu',
    page_id: PagesId.ManageItemsMedia,
    breadcrumbs: {
      home: 'Home',
      items_home: 'Products and inventory',
      manage_items_media: 'Upload and manage item images',
    },
    article: {
      title: 'Upload and manage item images',
      intended: [
        `<strong>Owners</strong> or <strong>Administrators</strong> with full access to manage <em>inventory</em> and <em>products</em> within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`,
        '<strong>Sellers</strong> or <strong>Suppliers</strong> with permissions to <em>create products</em>, <em>update product details</em>, and <em>assign images</em>.'
      ],
      about: {
        title: 'About upload and manage item images',
        content_type: ContentType.Paragraph,
        content: [
          "<strong>Product images</strong> are key to <em>showcasing your inventory attractively</em>, <em>building trust</em>, and increasing conversions in your online store. Uploading clear, professional, and up-to-date images not only improves your store's visual appearance but also helps customers make informed purchasing decisions.",
          'In this guide, you wll learn step by step how to upload new product images or update existing ones.'
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Before adding or updating product images, make sure you have the following:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Supported formats are <strong>PNG</strong>, <strong>JPEG</strong>, <strong>SVG</strong>, <strong>WEBP</strong>',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Max file size is 2 MB, but we recommend 500 kB or less for faster webpage loading speeds'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Recommended size is 450x450 pixels with a 1:1 aspect ratio for best clarity'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Keep original image copies</strong> in case you need to revert changes or fix issues.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            class: 'msg-alert',
            content: [
              `<strong>Nota</strong>: See our <a href=${process.env.STORES_WEBAPP_HOST} title="image formats and recommendations guide">guide on image formats and recommendations</a> for more tips on sizing, compression, and quality.`,
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Steps to upload images',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Inventory</em> section within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select a product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Search for the product in the list or use the search bar. Click the product name, then click the image icon located below the product title to enter <strong>image edit mode</strong>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Upload your images: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In image edit mode, click <em>"<strong>Select an image from your gallery</strong>"</em>. Depending on your device:'
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>iPhone or Android</b>: A menu will appear with options like <em>Take a photo</em> or <em>Choose from gallery</em>. Select your preferred method.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Laptop</b>: Your file explorer will open. Navigate to the folder where your image is stored and select it.',
                    ],
                  },
                ],
              },

            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Image is saved automatically: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Once selected, the image will upload and <strong>be saved automatically</strong> to the product.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Note</strong>: If you're on the <strong>Free plan</strong>, you can only upload <strong>one image per product</strong>. To add more, consider upgrading in Settings > Plans and Billing`,
                ],
              },
            ],
          },
        ],
      },
      how_to_update: {
        title: 'Steps to update images',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Inventory</em> section within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select a product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Search for the product in the list or use the search bar. Click the product name, then click the image icon located below the product title to enter <strong>image edit mode</strong>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Choose the image to update: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In the image carousel, click on the image you want to replace.',
                  'Once selected, press the +"<strong>Update image</strong>" button located at the bottom of the image.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select the new image:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Depending on the device you are using:',
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>iPhone and Android</b>: When you press the button, a menu will appear with options like <em>Take a photo</em> or <em>Choose from gallery</em>. Select the method you prefer to upload your image',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      "<b>Laptop</b>: Clicking the button will open your <strong>system's file explorer</strong>. Navigate to the appropriate folder and select the image you want to upload.",
                    ],
                  },
                ],
              },

            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Image saved automatically:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'The image will be replaced and automatically saved to the product.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Note</strong>: If you're on the Free plan, you can only have one image per product, so you'll need to replace the existing one if you want to update it.`,
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Steps to delete product images',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Inventory</em> section within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select a product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Search for the product in the list or use the search bar. Click the product name, then click the image icon located below the product title to enter <strong>image edit mode</strong>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Delete the desire image:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "In the image carousel, hover your cursor over the image (or tap it if you're on mobile), then click the trash icon in the top-right corner to delete it from the product."
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Note</strong>: Once deleted, the image cannot be recovered from wicadu. We recommend keeping a backup on your device in case you need it later.`,
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "Now that you know how to upload, update, and delete product images, consider taking the following actions to improve your store's presentation:"
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Review product details and images in your online store:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'After uploading, updating, or deleting images, check how your products appear in the storefront to ensure everything looks correct and appealing to your customers.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Consider upgrading your plan if you need more images:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Paid plans allow you to upload more than one image per product. If you are on the free plan, you can upgrade by going to <b>Settings</b> > <b>Plans and Billing</b>.'
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "<strong>I can't upload an image</strong>: Make sure the image is in <strong>PNG</strong>, <strong>JPEG</strong>, <strong>SVG</strong>, or <strong>WEBP</strong> format and weighs less than 2 MB.",
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>I can only upload one image per product</strong>: The <strong>free plan</strong> allows only one image per product. Upgrade your plan to add more.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              "<strong>I replaced an image but don't see the change</strong>: Try clearing your browser cache or reloading the page. Make sure you selected the correct image.",
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              "<strong>I uploaded an image but it looks blurry</strong>: Use an image that is at least 450x450 pixels and ideally under 500 kB for good quality and fast loading.",
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>The image won't delete</strong>: Check your internet connection. If the issue persists, contact the  
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.English)} title="wicadu Support">wicadu Support</a> Team for assistance.`,
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: 'Gérer les images des produits',
    seo_description:
      'Découvrez comment ajouter, mettre à jour et supprimer les images de vos produits dans wicadu.',
    seo_keywords:
      'images produits, médias produits, télécharger image produit, mettre à jour image produit, supprimer image produit, wicadu inventaire',
    page_id: PagesId.ManageItemsMedia,
    breadcrumbs: {
      home: 'Accueil',
      items_home: 'Produits et inventaire',
      manage_items_media: 'Téléverser et gérer les images des produits',
    },
    article: {
      title: 'Téléverser et gérer les images des produits',
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes qui gèrent le <em>catalogue produit</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        `<strong>Vendeurs</strong> ayant l’autorisation de <em>mettre à jour les fiches produit</em> et leur contenu visuel.`,
      ],
      about: {
        title: 'À propos des images produit',
        content_type: ContentType.Paragraph,
        content: [
          'Les <strong>images produit</strong> aident vos clients à identifier rapidement ce que vous vendez et améliorent la présentation de votre <strong>catalogue</strong> dans <strong>wicadu</strong>. Des visuels clairs renforcent la confiance, rendent la fiche produit plus attractive et facilitent la compréhension de chaque article avant l’achat.',
          'Une médiathèque bien tenue rend vos fiches plus claires, plus fiables et plus faciles à maintenir dans le temps. Elle vous aide aussi à mieux organiser vos <strong>produits</strong>, à mettre en avant les bonnes variantes et à offrir une meilleure expérience sur votre boutique en ligne.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: ['Avant d’ajouter ou de mettre à jour des images de produit, assurez-vous de disposer des éléments suivants :'],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              { content_type: ContentType.Paragraph, content: ['Vous pouvez téléverser des images pour vos produits tant qu’elles sont dans l’un de ces formats : PNG, JPEG, SVG, WEBP.'] },
              { content_type: ContentType.Paragraph, content: ['Les images peuvent peser jusqu’à 2 Mo, mais nous recommandons un poids de 500 ko ou moins afin que votre boutique en ligne charge plus rapidement.'] },
              { content_type: ContentType.Paragraph, content: ['La taille recommandée est de 450x450 pixels avec un ratio 1:1 pour une meilleure clarté.'] },
              { content_type: ContentType.Paragraph, content: ['Conservez <strong>des copies originales de vos images</strong> au cas où vous devriez annuler des changements ou résoudre un problème.'] },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            class: 'msg-alert',
            content: [
              `<strong>Remarque</strong> : consultez notre <a href=${process.env.STORES_WEBAPP_HOST} title="Guide des formats et recommandations pour les images">guide des formats et recommandations pour les images</a> pour plus de détails sur les dimensions, la compression et les bonnes pratiques.`,
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Étapes pour téléverser des images',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: [`Accédez à la fonctionnalité <em>Inventaire</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST} title="Assistant wicadu">assistant wicadu</a>.`] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner un produit : '] },
              { content_type: ContentType.Paragraph, content: ['Recherchez le produit dans la liste ou utilisez la barre de recherche. Cliquez sur le nom du produit, puis sur l’icône d’image située sous le titre du produit pour entrer en <strong>mode d’édition des images</strong>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Téléverser vos images : '] },
              { content_type: ContentType.Paragraph, content: ['En mode d’édition des images, cliquez sur <em>"<strong>Sélectionner une image depuis votre galerie</strong>"</em>. Selon votre appareil :'] },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>iPhone ou Android</b> : un menu apparaîtra avec des options comme <em>Prendre une photo</em> ou <em>Choisir depuis la galerie</em>. Sélectionnez la méthode de votre choix.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Ordinateur portable</b> : votre explorateur de fichiers s’ouvrira. Accédez au dossier où se trouve votre image et sélectionnez-la.',
                    ],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Image enregistrée automatiquement : '] },
              { content_type: ContentType.Paragraph, content: ['Une fois sélectionnée, l’image sera téléversée et <strong>enregistrée automatiquement</strong> sur le produit.'] },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Remarque</strong> : si vous utilisez le <strong>forfait gratuit</strong>, vous ne pouvez téléverser <strong>qu’une seule image par produit</strong>. Pour en ajouter davantage, envisagez de passer à une offre supérieure dans Paramètres > Forfaits et facturation.`,
                ],
              },
            ],
          },
        ],
      },
      how_to_update: {
        title: 'Étapes pour mettre à jour des images',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: [`Accédez à la fonctionnalité <em>Inventaire</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST} title="Assistant wicadu">assistant wicadu</a>.`] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner un produit : '] },
              { content_type: ContentType.Paragraph, content: ['Recherchez le produit dans la liste ou utilisez la barre de recherche. Cliquez sur le nom du produit, puis sur l’icône d’image située sous le titre du produit pour entrer en <strong>mode d’édition des images</strong>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Choisir l’image à mettre à jour :'] },
              { content_type: ContentType.Paragraph, content: ['Dans le carrousel d’images, cliquez sur l’image que vous souhaitez remplacer.', 'Une fois sélectionnée, appuyez sur le bouton <strong>"Mettre à jour l’image"</strong> situé au bas de l’image.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la nouvelle image :'] },
              { content_type: ContentType.Paragraph, content: ['Selon l’appareil que vous utilisez :'] },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>iPhone et Android</b> : lorsque vous appuyez sur le bouton, un menu apparaît avec des options comme <em>Prendre une photo</em> ou <em>Choisir depuis la galerie</em>. Sélectionnez la méthode que vous préférez pour téléverser votre image.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Ordinateur portable</b> : en cliquant sur le bouton, vous ouvrirez <strong>l’explorateur de fichiers de votre système</strong>. Accédez au dossier approprié et sélectionnez l’image que vous souhaitez téléverser.',
                    ],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Image enregistrée automatiquement :'] },
              { content_type: ContentType.Paragraph, content: ['L’image sera remplacée et enregistrée automatiquement sur le produit.'] },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Remarque</strong> : si vous utilisez le forfait gratuit, vous ne pouvez avoir qu’une seule image par produit. Vous devrez donc remplacer l’image existante si vous souhaitez la mettre à jour.`,
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Étapes pour supprimer des images produit',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: [`Accédez à la fonctionnalité <em>Inventaire</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST} title="Assistant wicadu">assistant wicadu</a>.`] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner un produit : '] },
              { content_type: ContentType.Paragraph, content: ['Recherchez le produit dans la liste ou utilisez la barre de recherche. Cliquez sur le nom du produit, puis sur l’icône d’image située sous le titre du produit pour entrer en <strong>mode d’édition des images</strong>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Supprimer l’image souhaitée :'] },
              { content_type: ContentType.Paragraph, content: ['Dans le carrousel d’images, placez votre curseur sur l’image (ou touchez-la si vous êtes sur mobile), puis cliquez sur l’icône de corbeille en haut à droite pour la supprimer du produit.'] },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  `<strong>Remarque</strong> : une fois supprimée, l’image ne peut pas être récupérée depuis wicadu. Nous vous recommandons de conserver une copie de sauvegarde sur votre appareil au cas où vous en auriez besoin plus tard.`,
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Maintenant que vous savez comment téléverser, mettre à jour et supprimer des images produit, envisagez les actions suivantes pour améliorer la présentation de votre boutique :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier les détails du produit et les images dans votre boutique en ligne :'] },
                  { content_type: ContentType.Paragraph, content: ['Après avoir téléversé, mis à jour ou supprimé des images, vérifiez comment vos produits apparaissent dans la boutique afin de vous assurer que tout est correct et attrayant pour vos clients.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Envisager de passer à une offre supérieure si vous avez besoin de plus d’images :'] },
                  { content_type: ContentType.Paragraph, content: ['Les forfaits payants vous permettent de téléverser plus d’une image par produit. Si vous utilisez le forfait gratuit, vous pouvez changer d’offre dans <b>Paramètres</b> > <b>Forfaits et facturation</b>.'] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Je ne peux pas téléverser une image</strong> : assurez-vous que l’image est au format <strong>PNG</strong>, <strong>JPEG</strong>, <strong>SVG</strong> ou <strong>WEBP</strong> et qu’elle pèse moins de 2 Mo.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Je peux seulement téléverser une image par produit</strong> : le <strong>forfait gratuit</strong> autorise uniquement une image par produit. Passez à une offre supérieure pour en ajouter davantage.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>J’ai remplacé une image mais je ne vois pas le changement</strong> : essayez de vider le cache de votre navigateur ou de recharger la page. Vérifiez que vous avez bien sélectionné la bonne image.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>J’ai téléversé une image mais elle apparaît floue</strong> : utilisez une image d’au moins 450x450 pixels et idéalement inférieure à 500 ko pour obtenir une bonne qualité et un chargement rapide.'] },
          { content_type: ContentType.Paragraph, content: [`<strong>L’image ne se supprime pas</strong> : vérifiez votre connexion internet. Si le problème persiste, contactez l’équipe de
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.French)} title="support wicadu">support wicadu</a> pour obtenir de l’aide.`] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
